import { safeHttpUrl } from "./safe-url";

const IMAGE_EXTENSIONS = Object.freeze(
  new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "avif"])
);

/**
 * Returns true if the filename has an image extension (case-insensitive).
 */
export function isImageFilename(filename: string): boolean {
  const dot = filename.lastIndexOf(".");
  if (dot === -1) return false;
  return IMAGE_EXTENSIONS.has(filename.slice(dot + 1).toLowerCase());
}

/**
 * Builds a local attachment-proxy URL for a FogBugz attachment sURL.
 *
 * Accepts both absolute URLs (https://fogbugz.example.com/default.asp?...)
 * and relative paths as returned by the FogBugz API (default.asp?...).
 *
 * - Ensures the pathname is exactly `/default.asp` (case-insensitive).
 * - Strips the `token` and `sTicket` query params from the forwarded path.
 * - Optionally adds `inline=true` so the proxy serves with
 *   `Content-Disposition: inline` for image previews.
 *
 * Returns `null` for URLs that don't match the FogBugz download pattern.
 */
export function buildAttachmentProxyUrl(
  sURL: string,
  options?: { inline?: boolean }
): string | null {
  if (!sURL) return null;

  // FogBugz API returns sURL with HTML-encoded ampersands (&amp; instead of &)
  const decoded = sURL.replace(/&amp;/g, "&");

  let parsed: URL;
  try {
    // Try as absolute first
    const safe = safeHttpUrl(decoded);
    if (safe) {
      parsed = new URL(safe);
    } else {
      // FogBugz often returns relative URLs like "default.asp?pg=pgDownload&..."
      // Resolve against a dummy base to parse the path and query
      parsed = new URL(decoded, "https://placeholder.invalid");
    }
  } catch {
    return null;
  }

  // Only proxy FogBugz /default.asp?pg=pgDownload attachment URLs
  if (parsed.pathname.replace(/^\//, "").toLowerCase() !== "default.asp") {
    return null;
  }
  const pg = parsed.searchParams.get("pg");
  if (pg !== "pgDownload") {
    return null;
  }
  // Require at least one attachment identifier param with a non-blank value
  // (matches API schema)
  const hasIdentifier = [
    "ixAttachment",
    "ixBugAttachment",
    "ixBugEventAttachment",
    "sFileName",
  ].some((key) => (parsed.searchParams.get(key)?.trim().length ?? 0) > 0);
  if (!hasIdentifier) {
    return null;
  }

  // Strip token and ticket params to avoid leaking credentials
  parsed.searchParams.delete("token");
  parsed.searchParams.delete("sTicket");

  // Reconstruct the path + query that the proxy expects (always with leading slash)
  const pathname = parsed.pathname.startsWith("/")
    ? parsed.pathname
    : `/${parsed.pathname}`;
  const pathAndQuery = `${pathname}${parsed.search}`;

  const proxyParams = new URLSearchParams();
  proxyParams.set("path", pathAndQuery);
  if (options?.inline) {
    proxyParams.set("inline", "true");
  }

  return `/api/v1/fogbugz/attachment-proxy?${proxyParams.toString()}`;
}
